import React from 'react';
import { CheckCircle2, MicOff, Lightbulb, Target, TrendingUp, Gauge, ThumbsUp, ThumbsDown, Star } from 'lucide-react';
import { AiInterviewResult } from '../../types';
import { RECOMMENDATION_META } from './interviewConstants';

interface Props {
  result: AiInterviewResult;
}

const SCORE_META = [
  { label: 'Technical', icon: Gauge },
  { label: 'Communication', icon: TrendingUp },
  { label: 'Problem solving', icon: Target },
];

const scoreBars = (result: AiInterviewResult) => {
  const items = [
    { label: 'Technical', value: result.technicalScore },
    { label: 'Communication', value: result.communicationScore },
    { label: 'Problem solving', value: result.problemSolvingScore },
  ];
  const overall = result.overallScore;
  return { overall, items };
};

const InterviewResultCard: React.FC<Props> = ({ result }) => {
  const recommendationMeta = RECOMMENDATION_META[result.recommendation];
  const { overall, items } = scoreBars(result);

  if (overall == null) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="border border-zinc-200 rounded-card shadow-soft bg-white p-6 text-center">
          <div className="w-14 h-14 rounded-btn bg-amber-50 flex items-center justify-center mx-auto mb-4">
            <MicOff className="w-7 h-7 text-amber-500" />
          </div>
          <p className="font-display font-semibold text-foreground text-lg leading-tight">Interview complete</p>
          <p className="text-sm text-zinc-500 mt-1 max-w-md mx-auto">{result.summary}</p>
        </div>
      </div>
    );
  }

  const bar = (label: string, value: number, icon: React.ReactNode) => (
    <div className="flex items-center gap-3">
      <span className="w-8 h-8 rounded-btn bg-zinc-50 border border-zinc-200 flex items-center justify-center text-zinc-400 shrink-0">
        {icon}
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-1">
          <p className="text-xs font-medium text-zinc-600">{label}</p>
          <p className="text-xs font-semibold text-foreground">{Math.round(value)}%</p>
        </div>
        <div className="h-1.5 bg-zinc-100 rounded-full overflow-hidden">
          <div className="h-full bg-brand-500 rounded-full transition-all" style={{ width: `${Math.min(100, Math.max(0, value))}%` }} />
        </div>
      </div>
    </div>
  );

  return (
    <div className="max-w-2xl mx-auto space-y-4">
      <div className="border border-zinc-200 rounded-card shadow-soft bg-white p-6">
        <div className="flex items-center justify-between gap-4 flex-wrap mb-5">
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 rounded-btn bg-emerald-50 flex items-center justify-center">
              <CheckCircle2 className="w-7 h-7 text-emerald-500" />
            </div>
            <div>
              <p className="font-display font-semibold text-foreground text-lg leading-tight">Interview complete</p>
              <p className="text-sm text-zinc-500">Evaluation saved to this application.</p>
            </div>
          </div>
          <div className={`text-xs font-bold px-3 py-1.5 rounded-full ${recommendationMeta.badge}`}>
            {recommendationMeta.label}
          </div>
        </div>

        <div className="flex items-center gap-4 mb-1">
          <div className="flex items-baseline gap-1.5">
            <span className="font-display font-bold text-foreground text-4xl leading-none">{Math.round(overall)}</span>
            <span className="text-sm text-zinc-400">/ 100</span>
          </div>
          <div className="flex items-center gap-0.5">
            {[0, 1, 2, 3, 4].map((i) => (
              <Star
                key={i}
                className={`w-4 h-4 ${i < Math.round(overall / 20) ? 'fill-amber-400 text-amber-400' : 'text-zinc-200'}`}
              />
            ))}
          </div>
          <p className="text-sm text-zinc-500">Overall fit</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
          {items.map(({ label, value }) => bar(label, value, React.createElement(SCORE_META.find((m) => m.label === label)!.icon, { className: 'w-4 h-4', size: 16 })))}
        </div>
      </div>

      {result.summary && (
        <div className="border border-zinc-200 rounded-card shadow-soft bg-white p-6">
          <p className="text-xs font-semibold text-foreground uppercase tracking-wide mb-2">Summary</p>
          <p className="text-sm text-zinc-600 leading-relaxed whitespace-pre-wrap">{result.summary}</p>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {result.strengths.length > 0 && (
          <div className="border border-zinc-200 rounded-card shadow-soft bg-white p-5">
            <p className="text-xs font-semibold text-foreground uppercase tracking-wide mb-3 inline-flex items-center gap-1.5">
              <ThumbsUp className="w-3.5 h-3.5 text-emerald-500" /> Strengths
            </p>
            <ul className="space-y-1.5">
              {result.strengths.map((s, i) => (
                <li key={i} className="text-sm text-zinc-600 flex gap-2">
                  <span className="text-emerald-500 mt-0.5">•</span> {s}
                </li>
              ))}
            </ul>
          </div>
        )}
        {result.weaknesses.length > 0 && (
          <div className="border border-zinc-200 rounded-card shadow-soft bg-white p-5">
            <p className="text-xs font-semibold text-foreground uppercase tracking-wide mb-3 inline-flex items-center gap-1.5">
              <ThumbsDown className="w-3.5 h-3.5 text-amber-500" /> Growth areas
            </p>
            <ul className="space-y-1.5">
              {result.weaknesses.map((w, i) => (
                <li key={i} className="text-sm text-zinc-600 flex gap-2">
                  <span className="text-amber-500 mt-0.5">•</span> {w}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {result.questionEvaluations.length > 0 && (
        <div className="border border-zinc-200 rounded-card shadow-soft bg-white p-6">
          <p className="text-xs font-semibold text-foreground uppercase tracking-wide mb-3">
            Question breakdown — how to improve
          </p>
          <ul className="space-y-3">
            {result.questionEvaluations.map((q, i) => (
              <li key={i} className="flex items-start gap-3 border-t border-zinc-100 pt-3 first:border-0 first:pt-0">
                <span className="text-xs font-bold text-brand-600 bg-brand-50 rounded-btn px-2 py-0.5 w-9 text-center shrink-0">
                  {Math.round(q.score)}%
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground">{q.question}</p>
                  {q.feedback && <p className="text-xs text-zinc-500 mt-0.5">{q.feedback}</p>}
                  {q.improvement && (
                    <p className="text-xs text-zinc-600 mt-1.5 inline-flex items-start gap-1.5">
                      <Lightbulb className="w-3.5 h-3.5 text-amber-500 mt-0.5 shrink-0" />
                      <span>{q.improvement}</span>
                    </p>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default InterviewResultCard;